import { useState, useEffect } from 'react';

interface Story {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string;
  userLevel: number;
  stories: {
    id: string;
    type: 'image' | 'achievement';
    imageUrl?: string;
    achievement?: {
      type: 'badge' | 'level' | 'mission';
      title: string;
      icon: string;
    };
    timestamp: Date;
    views: number;
  }[];
  hasViewed: boolean;
}

interface StoryViewerProps {
  story: Story;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

export function StoryViewer({ story, onClose, onNext, onPrevious }: StoryViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const currentStory = story.stories[currentIndex];
  const duration = 5000; // 5 seconds per story

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          if (currentIndex < story.stories.length - 1) {
            setCurrentIndex(currentIndex + 1);
            return 0;
          } else {
            onNext();
            return 100;
          }
        }
        return prev + (100 / (duration / 100));
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentIndex, isPaused, story.stories.length, onNext]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setProgress(0);
    } else {
      onPrevious();
    }
  };

  const handleNext = () => {
    if (currentIndex < story.stories.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setProgress(0);
    } else {
      onNext();
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors z-10"
      >
        <i className="ri-close-line text-2xl"></i>
      </button>

      {/* Navigation Areas */}
      <div className="absolute inset-0 flex">
        <button
          onClick={handlePrevious}
          className="flex-1 cursor-pointer"
          onMouseDown={() => setIsPaused(true)}
          onMouseUp={() => setIsPaused(false)}
          onMouseLeave={() => setIsPaused(false)}
        />
        <button
          onClick={handleNext}
          className="flex-1 cursor-pointer"
          onMouseDown={() => setIsPaused(true)}
          onMouseUp={() => setIsPaused(false)}
          onMouseLeave={() => setIsPaused(false)}
        />
      </div>

      {/* Story Content */}
      <div className="relative w-full max-w-md h-full max-h-[90vh] bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl overflow-hidden shadow-2xl">
        {/* Progress Bars */}
        <div className="absolute top-0 left-0 right-0 flex gap-1 p-2 z-10">
          {story.stories.map((_, index) => (
            <div key={index} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-white transition-all duration-100"
                style={{
                  width: index < currentIndex ? '100%' : index === currentIndex ? `${progress}%` : '0%'
                }}
              />
            </div>
          ))}
        </div>

        {/* User Info */}
        <div className="absolute top-4 left-4 right-16 flex items-center gap-3 z-10">
          <div className="relative">
            <img
              src={story.userAvatar}
              alt={story.userName}
              className="w-10 h-10 rounded-full object-cover border-2 border-white"
            />
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
              {story.userLevel}
            </div>
          </div>
          <div>
            <p className="text-white font-bold text-sm">{story.userName}</p>
            <p className="text-white/70 text-xs">
              {Math.floor((Date.now() - currentStory.timestamp.getTime()) / (1000 * 60 * 60))}h atrás
            </p>
          </div>
        </div>

        {/* Story Content */}
        <div className="w-full h-full flex items-center justify-center p-8">
          {currentStory.type === 'achievement' && currentStory.achievement && (
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-3xl flex items-center justify-center shadow-2xl animate-bounce">
                <i className={`${currentStory.achievement.icon} text-6xl text-white`}></i>
              </div>
              <h3 className="text-3xl font-black text-white mb-2">
                {currentStory.achievement.title}
              </h3>
              <p className="text-white/70 text-lg">
                {currentStory.achievement.type === 'badge' && 'Novo Distintivo Desbloqueado!'}
                {currentStory.achievement.type === 'level' && 'Novo Nível Alcançado!'}
                {currentStory.achievement.type === 'mission' && 'Missão Concluída!'}
              </p>
            </div>
          )}
        </div>

        {/* Views Counter */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white/80">
          <i className="ri-eye-line"></i>
          <span className="text-sm font-medium">{currentStory.views} visualizações</span>
        </div>
      </div>
    </div>
  );
}
